import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ONBOARDING_TRAINING_PLAN_READY_SRC,
  ONBOARDING_TRAINING_PLAN_READY_SRC_2X,
  OnboardingRetinaImage,
} from '../onboardingAssets';
import { useOnboardingLens } from '../onboardingLens';

interface OnboardingStepCompleteProps {
  /** True when onboarding ran with AI and a draft training plan was generated. */
  hasTrainingPlan?: boolean;
}

/** Figma step 7 — success empty state (with vs without training plan). */
export function OnboardingStepComplete({ hasTrainingPlan = false }: OnboardingStepCompleteProps) {
  const { t } = useTranslation();
  const { layout, typography, colors } = useOnboardingLens();
  const { complete } = layout;

  const title = hasTrainingPlan
    ? t('onboarding.wizard.step7.title_with_plan')
    : t('onboarding.wizard.step7.title');
  const subtitle = hasTrainingPlan
    ? t('onboarding.wizard.step7.subtitle_with_plan')
    : t('onboarding.wizard.step7.subtitle');

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: 0,
        height: '100%',
        backgroundColor: colors.surfaceDefault,
      }}
    >
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 'none',
          width: '100%',
          maxWidth: complete.emptyStateMaxWidth,
          py: complete.emptyStatePaddingY,
          gap: complete.illustrationGap,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: complete.illustrationWidth,
            height: complete.illustrationHeight,
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <OnboardingRetinaImage
            src={ONBOARDING_TRAINING_PLAN_READY_SRC}
            src2x={ONBOARDING_TRAINING_PLAN_READY_SRC_2X}
            width={complete.illustrationWidth}
            height={complete.illustrationHeight}
            sx={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: complete.contentGap,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              m: 0,
              width: '100%',
              ...typography.h2Display,
              color: colors.typeDefault,
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
          <Typography
            component="p"
            sx={{
              m: 0,
              width: '100%',
              ...typography.body,
              color: colors.typeDefault,
              textAlign: 'center',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
